import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Spinner,
  useToast,
  Divider,
  Input,
  Collapse,
  SimpleGrid,
  Checkbox,
  Progress,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react";
import PhoneStatusBadge from "./sms/PhoneStatusBadge";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import { BASE_URLS, bucketName_files, prompts } from "./env";
import {
  fetchDocument,
  generateMedADocsPath,
} from "../documentComponents/documentUtilities";
import {
  saveUserTemplate,
  loadSavedUserTemplates,
  loadUserDefaultTemplate,
  deleteUserTemplate,
  updateUserTemplate,
  setDefaultTemplate,
  clearDefaultTemplate,
  saveUserDefaultTemplate,
  getTemplateTypePrefix,
  getTemplateFolderName,
  getCurrentUserName,
  loadGlobalSystemTemplate,
  shareTemplateToShared,
  loadSharedTemplates,
  importSharedTemplate,
  removeSharedTemplate,
  loadUserDefaultTemplateFileName,
} from "../utils/formProcessingUtils";
import GlobalSystemPromptManager from "./GlobalSystemPromptManager";
import { confirmBadNumbers } from "../utils/badNumbers";

/**
 * FollowupMessageModal - A modal for generating and sending AI-powered follow-up messages
 * Includes collapsible prompt editor in the same view as the message
 */
const FollowupMessageModal = ({
  isOpen,
  onClose,
  patientData,
  schema,
  onSend,
}) => {
  // Use supabaseSchema (single source of truth) as primary, fallback to passed schema prop
  // This ensures we always use the current user's schema even if the prop hasn't updated yet
  const effectiveSchema = supabaseSchema || schema;

  // Debug log to help troubleshoot schema issues
  console.log("[FollowupMessageModal] Schema debug:", {
    passedSchema: schema,
    supabaseSchema,
    effectiveSchema,
  });

  const toast = useToast();

  // Determine if we're handling multiple patients
  const isMultiplePatients =
    patientData && patientData.patients && Array.isArray(patientData.patients);

  // Get the list of patients (either from array or single patient object)
  const patientsList = isMultiplePatients
    ? patientData.patients
    : patientData
      ? [patientData]
      : [];

  // State for tracking which patients should receive the message
  // Initialize with empty array - will be set by useEffect when modal opens
  const [selectedPatientsForSending, setSelectedPatientsForSending] = useState(
    [],
  );

  // State for tracking send progress (for multiple patients)
  const [sendProgress, setSendProgress] = useState({
    current: 0,
    total: 0,
    isSending: false,
    completed: [],
    failed: [],
  });

  // State for tab navigation (for multiple patients)
  const [activePatientIndex, setActivePatientIndex] = useState(0);

  // State for storing individual messages per patient
  // Format: { [patientId]: { generated: "", edited: "" } }
  const [patientMessages, setPatientMessages] = useState({});

  // State for tracking which patients have had their messages generated
  const [generatedPatients, setGeneratedPatients] = useState([]);

  // State for tracking generation progress (for multiple patients)
  const [generationProgress, setGenerationProgress] = useState({
    current: 0,
    total: 0,
    isGenerating: false,
  });

  // Separate state for bulk generation to avoid conflict with single generation
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);

  // State - reset when patientData changes
  useEffect(() => {
    // Reset selection when modal opens with new patient data
    if (isOpen && patientsList.length > 0) {
      setSelectedPatientsForSending(
        patientsList.filter((p) => p.patientPhone).map((p) => p.patientId),
      );
      // Reset progress state
      setSendProgress({
        current: 0,
        total: 0,
        isSending: false,
        completed: [],
        failed: [],
      });
      // Reset tab navigation
      setActivePatientIndex(0);
      // Reset patient messages
      setPatientMessages({});
      setGeneratedPatients([]);
      // Reset generation progress
      setGenerationProgress({ current: 0, total: 0, isGenerating: false });
    }
  }, [isOpen, patientData]);

  // State
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [editedMessage, setEditedMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [documentContent, setDocumentContent] = useState("");
  const [isFetchingDoc, setIsFetchingDoc] = useState(false);
  const messageTextareaRef = useRef(null);

  // Custom prompt state
  const [customPrompt, setCustomPrompt] = useState(null);
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  // Prompt editor state
  const [editablePrompt, setEditablePrompt] = useState("");
  const [templateInfo, setTemplateInfo] = useState("");
  const [savedFollowupTemplates, setSavedFollowupTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [editingTemplateFileName, setEditingTemplateFileName] = useState(null);
  const [defaultTemplateFileName, setDefaultTemplateFileName] = useState(null);
  const [showGlobalPromptManager, setShowGlobalPromptManager] = useState(false);

  // Shared templates state
  const [sharedTemplates, setSharedTemplates] = useState([]);
  const [showShared, setShowShared] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  // Track shared templates with their authors: { fileName: { fileName, author } }
  const [sharedTemplatesMap, setSharedTemplatesMap] = useState({});
  const [currentUserName, setCurrentUserName] = useState(null);

  // Track the file name of the user's default template separately
  const [userDefaultFileName, setUserDefaultFileName] = useState(null);

  // Load saved follow-up templates
  // Accepts optional callback to be called with the loaded prompt data after templates are loaded
  const loadFollowupTemplates = async (onLoaded) => {
    let loadedPromptData = null;

    try {
      const templates = await loadSavedUserTemplates("followup");
      setSavedFollowupTemplates(templates || []);

      // FIRST: Load the user's default template file name (if saved)
      const defaultFileNameFromStorage =
        await loadUserDefaultTemplateFileName("followup");
      console.log(
        "[FollowupModal] Loaded default file name from storage:",
        defaultFileNameFromStorage,
      );

      // Also get the content from the default files
      const userDefault = await loadUserDefaultTemplate(
        "followup",
        "Follow-up",
      );

      // Determine which template to use: user default > global > system default
      // Priority: user default > global system template > hardcoded default
      let finalTemplateText = null;
      let finalPromptText = null;
      let templateSource = "system";

      // If we have a saved default file name, try to find the template and use its content
      if (defaultFileNameFromStorage && templates) {
        const matchingTemplate = templates.find(
          (t) => t.fileName === defaultFileNameFromStorage,
        );
        if (matchingTemplate) {
          console.log(
            "[FollowupModal] Found template by file name:",
            matchingTemplate.name,
          );
          finalTemplateText = matchingTemplate.template;
          finalPromptText = matchingTemplate.prompt;
          templateSource = "user";
          // Set userDefaultFileName immediately
          setUserDefaultFileName(defaultFileNameFromStorage);

          // Also set the prompt/template content from this template
          setEditablePrompt(matchingTemplate.prompt || "");
          setTemplateInfo(matchingTemplate.template || "");
          setTemplateName(matchingTemplate.name || "Default");
          setEditingTemplateFileName(matchingTemplate.fileName);

          const promptData = {
            fullPrompt: matchingTemplate.prompt || "",
            editablePrompt: matchingTemplate.prompt || "",
            templateInfo: matchingTemplate.template || "",
          };
          setCustomPrompt(promptData);
          loadedPromptData = promptData;

          console.log(
            "[FollowupModal] Using template content from file name match",
          );

          // Call the callback and return early
          if (onLoaded && typeof onLoaded === "function") {
            setTimeout(() => {
              onLoaded(loadedPromptData);
            }, 0);
          }
          return;
        }
      }

      // Fall back to using userDefault content (from default files)
      if (userDefault.templateText && userDefault.promptText) {
        // User has a personal default template - use it (highest priority for defaults)
        finalTemplateText = userDefault.templateText;
        finalPromptText = userDefault.promptText;
        templateSource = "user";

        console.log("[FollowupModal] Using USER default template");
      } else {
        // No user default - check for global system template (as fallback default)
        const globalTemplate = await loadGlobalSystemTemplate("followup");

        if (globalTemplate.templateText && globalTemplate.promptText) {
          // Use global system template (only when no user default exists)
          finalTemplateText = globalTemplate.templateText;
          finalPromptText = globalTemplate.promptText;
          templateSource = "global";

          console.log(
            "[FollowupModal] Using GLOBAL system template (no user default)",
          );
        } else {
          // Fall back to system default (hardcoded)
          finalTemplateText = null;
          finalPromptText = null;
          templateSource = "system";

          console.log("[FollowupModal] Using hardcoded system default");
        }
      }

      // Also load global template for reference
      const globalTemplate = await loadGlobalSystemTemplate("followup");

      console.log("[FollowupModal] loadFollowupTemplates debug:", {
        templatesCount: templates?.length || 0,
        templateSource,
        userDefault: userDefault.templateText ? "present" : "null",
        userDefaultText: userDefault.templateText?.substring(0, 30),
        globalTemplate: globalTemplate.templateText ? "present" : "null",
        defaultFileName,
      });

      // Try to find which template matches the default by comparing content
      // For global templates, we don't have a saved template file, so defaultFileName stays null
      let defaultFileName = null;

      console.log("[FollowupModal] Looking for matching template:", {
        templateSource,
        finalTemplateText: finalTemplateText?.substring(0, 50),
        finalPromptText: finalPromptText?.substring(0, 50),
        templatesCount: templates?.length || 0,
      });

      // Only try to match user default templates (not global)
      if (
        templateSource === "user" &&
        finalTemplateText &&
        templates &&
        templates.length > 0
      ) {
        // Log each template for debugging
        templates.forEach((t, idx) => {
          const templateMatch = t.template === finalTemplateText;
          const promptMatch = t.prompt === finalPromptText;
          console.log(
            `[FollowupModal] Template ${idx}: "${t.name}" - template match: ${templateMatch}, prompt match: ${promptMatch}`,
          );
          if (templateMatch && !promptMatch) {
            console.log(
              `[FollowupModal] Template content matches but prompt doesn't!`,
            );
            console.log(`  Saved prompt: "${t.prompt?.substring(0, 50)}"`);
            console.log(
              `  Default prompt: "${finalPromptText?.substring(0, 50)}"`,
            );
          }
        });

        const matchingTemplate = templates.find(
          (t) =>
            t.template === finalTemplateText && t.prompt === finalPromptText,
        );
        if (matchingTemplate) {
          defaultFileName = matchingTemplate.fileName;
          console.log(
            "[FollowupModal] Found matching template:",
            defaultFileName,
          );
        } else {
          console.log("[FollowupModal] No matching template found!");

          // Try to find a template with matching template content at least
          const partialMatch = templates.find(
            (t) => t.template === finalTemplateText,
          );
          if (partialMatch) {
            console.log(
              "[FollowupModal] Found partial match (template only):",
              partialMatch.fileName,
            );
          }
        }
      } else if (templateSource === "global") {
        console.log(
          "[FollowupModal] Using global template - no file name for badge",
        );
      }

      console.log("[FollowupModal] Final defaultFileName:", defaultFileName);
      setDefaultTemplateFileName(defaultFileName);

      // If we found a partial match (template content matches), use that template's content
      // This ensures both the badge shows AND the prompt matches the selected default
      console.log("[FollowupModal] Checking for matching template:", {
        templateSource,
        finalTemplateText: finalTemplateText?.substring(0, 50),
        finalPromptText: finalPromptText?.substring(0, 50),
        templatesCount: templates?.length,
      });

      if (templateSource === "user" && finalTemplateText && templates) {
        const matchingTemplate = templates.find(
          (t) => t.template === finalTemplateText,
        );
        console.log(
          "[FollowupModal] Matching template search result:",
          matchingTemplate?.name || "not found",
        );

        if (matchingTemplate) {
          console.log(
            "[FollowupModal] Found matching template, using its content:",
            matchingTemplate.fileName,
          );
          console.log(
            "[FollowupModal] Matched template prompt preview:",
            matchingTemplate.prompt?.substring(0, 50),
          );

          setUserDefaultFileName(matchingTemplate.fileName);
          // Use the actual saved template's content instead of the default file content
          setEditablePrompt(matchingTemplate.prompt || "");
          setTemplateInfo(matchingTemplate.template || "");
          setTemplateName(matchingTemplate.name || "Default");
          setEditingTemplateFileName(matchingTemplate.fileName);
          const promptData = {
            fullPrompt: matchingTemplate.prompt || "",
            editablePrompt: matchingTemplate.prompt || "",
            templateInfo: matchingTemplate.template || "",
          };
          setCustomPrompt(promptData);
          loadedPromptData = promptData;

          // Return early to prevent code below from overwriting with default file content
          console.log("[FollowupModal] Returning early with matched template");
          if (onLoaded && typeof onLoaded === "function") {
            setTimeout(() => {
              onLoaded(loadedPromptData);
            }, 0);
          }
          return;
        } else {
          console.log(
            "[FollowupModal] No matching template found - will use default file content",
          );
        }
      }

      // Now set the state based on the selected template
      // Priority: user default > global > system default
      if (templateSource === "user") {
        // User has a personal default template - use it (highest priority)
        setEditablePrompt(finalPromptText);
        setTemplateInfo(finalTemplateText);
        setTemplateName("Default");
        setEditingTemplateFileName(null);

        console.log("[FollowupModal] Setting customPrompt from USER default");
        const promptData = {
          fullPrompt: finalPromptText,
          editablePrompt: finalPromptText,
          templateInfo: finalTemplateText,
        };
        setCustomPrompt(promptData);
        loadedPromptData = promptData;
      } else if (templateSource === "global") {
        // Use global system template (only when no user default exists)
        setEditablePrompt(finalPromptText);
        setTemplateInfo(finalTemplateText);
        setTemplateName("Global System");
        setEditingTemplateFileName(null);

        console.log(
          "[FollowupModal] Setting customPrompt from GLOBAL system template",
        );
        const promptData = {
          fullPrompt: finalPromptText,
          editablePrompt: finalPromptText,
          templateInfo: finalTemplateText,
        };
        setCustomPrompt(promptData);
        loadedPromptData = promptData;
      } else {
        // Use default prompts - system default (no customPrompt set, will use default prompt on backend)
        setEditablePrompt(getDefaultPrompt());
        setTemplateInfo(getDefaultTemplate());
        setTemplateName("");
        setEditingTemplateFileName(null);
        setCustomPrompt(null);
        loadedPromptData = null;
      }

      // Call the callback if provided with the loaded prompt data
      if (onLoaded && typeof onLoaded === "function") {
        // Use setTimeout to ensure React state has been updated before calling callback
        setTimeout(() => {
          onLoaded(loadedPromptData);
        }, 0);
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      setEditablePrompt(getDefaultPrompt());
      setTemplateInfo(getDefaultTemplate());
      setTemplateName("");
      setEditingTemplateFileName(null);
      setCustomPrompt(null);

      // Still call callback with null if error
      if (onLoaded && typeof onLoaded === "function") {
        setTimeout(() => {
          onLoaded(null);
        }, 0);
      }
    }
  };

  const getDefaultPrompt = () => {
    return `Based on the following patient information, generate a professional follow-up text message:

Patient Name: {patient_name}
Provider Name: {provider_name}
{patient_context}

Generate a detailed, professional follow-up message appropriate for the patient's condition and visit type. The message should:
1. Thank the patient for their visit
2. Mention any important follow-up items or pending results
3. Provide clear next steps
4. Include a friendly closing from {provider_name}

IMPORTANT: Use the exact provider name "{provider_name}" in the message. Do NOT use placeholders.

Generate a detailed message - don't limit the length.

Generate only the message text, no explanations:`;
  };

  const getDefaultTemplate = () => {
    return `Appointment Type: {appointment_type}
Recent Appointment Date: {recent_date}
SOAP Notes: {soap_notes}
Pending Follow-ups: {pending_followups}
Form Submissions: {form_submissions}`;
  };

  const handleSavePromptTemplate = async () => {
    if (!templateName.trim()) {
      toast({
        title: "Template Name Required",
        description: "Please enter a name for this template",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      await saveUserTemplate(
        "followup",
        templateInfo,
        editablePrompt,
        templateName,
      );
      toast({
        title: "Template Saved",
        description: "Your template has been saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setTemplateName("");
      loadFollowupTemplates();
    } catch (error) {
      toast({
        title: "Save Error",
        description: `Failed to save template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUsePrompt = () => {
    const promptData = {
      fullPrompt: editablePrompt,
      editablePrompt: editablePrompt,
      templateInfo: templateInfo,
    };
    setCustomPrompt(promptData);
    fetchDocumentAndGenerate(promptData);
    toast({
      title: "Generating",
      description: "Generating follow-up with custom prompt",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const loadTemplate = (fileName) => {
    const template = savedFollowupTemplates.find(
      (t) => t.fileName === fileName,
    );
    if (template) {
      setEditablePrompt(template.prompt || "");
      setTemplateInfo(template.template || "");
      setTemplateName(template.name || "");
      setEditingTemplateFileName(template.fileName);

      // Also set customPrompt so this template is used immediately for generation
      const promptData = {
        fullPrompt: template.prompt || "",
        editablePrompt: template.prompt || "",
        templateInfo: template.template || "",
      };
      setCustomPrompt(promptData);

      // Regenerate message immediately with the selected template
      // Only if we already have document content (modal is open)
      if (documentContent) {
        fetchDocumentAndGenerate(promptData);
      }
    }
  };

  const handleEditTemplate = async () => {
    if (!editingTemplateFileName) {
      toast({
        title: "No Template Selected",
        description: "Please select a template to edit",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!templateName.trim()) {
      toast({
        title: "Template Name Required",
        description: "Please enter a name for this template",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      // Pass the template name to updateUserTemplate so it can be stored in the header
      await updateUserTemplate(
        "followup",
        templateInfo,
        editablePrompt,
        editingTemplateFileName,
        templateName.trim(),
      );

      // Small delay to ensure storage is updated
      await new Promise((resolve) => setTimeout(resolve, 200));

      toast({
        title: "Template Updated",
        description: "Your template has been updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadFollowupTemplates();
    } catch (error) {
      toast({
        title: "Update Error",
        description: `Failed to update template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteTemplate = async (fileName) => {
    try {
      await deleteUserTemplate("followup", fileName);
      toast({
        title: "Template Deleted",
        description: "The template has been deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Clear editing state if deleted template was being edited
      if (editingTemplateFileName === fileName) {
        setEditingTemplateFileName(null);
        setTemplateName("");
      }

      loadFollowupTemplates();
    } catch (error) {
      toast({
        title: "Delete Error",
        description: `Failed to delete template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle sharing a specific template to shared templates
  const handleShareTemplate = async (template) => {
    setIsSharing(true);
    try {
      await shareTemplateToShared(
        "followup",
        template.template,
        template.prompt,
        template.name,
      );

      toast({
        title: "Template Shared!",
        description: `"${template.name}" is now available in the Shared Templates for all users`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Refresh shared templates
      loadSharedTemplatesFromShared();
    } catch (error) {
      toast({
        title: "Share Error",
        description: error.message || "Failed to share template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSharing(false);
    }
  };

  // Handle unsharing a template from shared templates
  const handleUnshareTemplate = async (template) => {
    setIsSharing(true);
    try {
      await removeSharedTemplate("followup", template.fileName);

      toast({
        title: "Template Unshared",
        description: `"${template.name}" has been removed from Shared Templates`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Refresh shared templates
      loadSharedTemplatesFromShared();
    } catch (error) {
      toast({
        title: "Unshare Error",
        description: error.message || "Failed to unshare template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleSetDefault = async (fileName) => {
    try {
      // Get the template to get its content for saving
      const template = savedFollowupTemplates.find(
        (t) => t.fileName === fileName,
      );

      // Save the template content as default AND save the file name
      if (template) {
        await saveUserDefaultTemplate(
          "followup",
          template.template || "",
          template.prompt || "",
          fileName, // Pass the fileName to be saved
        );
      }

      await setDefaultTemplate("followup", fileName);
      setDefaultTemplateFileName(fileName);
      // Also track the user's default template file name for badge display
      setUserDefaultFileName(fileName);

      // Already have the template from above, use it directly
      if (template) {
        setEditablePrompt(template.prompt || "");
        setTemplateInfo(template.template || "");
        setTemplateName(template.name || "");
        setEditingTemplateFileName(template.fileName);

        // Set customPrompt so the new default is used for generation
        setCustomPrompt({
          fullPrompt: template.prompt || "",
          editablePrompt: template.prompt || "",
          templateInfo: template.template || "",
        });
      }

      toast({
        title: "Default Template Set",
        description: "This template is now your default",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to set default template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle sharing a template to shared templates
  const handleShareToShared = async () => {
    if (!templateName.trim()) {
      toast({
        title: "Template Name Required",
        description: "Please enter a name for this template to share",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!editablePrompt.trim() || !templateInfo.trim()) {
      toast({
        title: "Template Content Required",
        description:
          "Please create a template with prompt and content before sharing",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSharing(true);
    try {
      await shareTemplateToShared(
        "followup",
        templateInfo,
        editablePrompt,
        templateName,
      );

      toast({
        title: "Template Shared!",
        description: `"${templateName}" is now available in the Shared Templates for all users`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Refresh shared templates
      loadSharedTemplatesFromShared();
    } catch (error) {
      toast({
        title: "Share Error",
        description: error.message || "Failed to share template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSharing(false);
    }
  };

  // Load shared templates
  const loadSharedTemplatesFromShared = async () => {
    setIsLoadingShared(true);
    try {
      const templates = await loadSharedTemplates("followup");
      setSharedTemplates(templates || []);
      // Build a map of fileName -> { fileName, author } for quick lookup
      const templateMap = {};
      (templates || []).forEach((t) => {
        templateMap[t.fileName] = { fileName: t.fileName, author: t.author };
      });
      setSharedTemplatesMap(templateMap);
    } catch (error) {
      console.error("Error loading shared templates:", error);
      setSharedTemplates([]);
      setSharedTemplatesMap({});
    } finally {
      setIsLoadingShared(false);
    }
  };

  // Handle importing a shared template
  const handleImportTemplate = async (sharedTemplate) => {
    try {
      const result = await importSharedTemplate(
        "followup",
        sharedTemplate.fileName,
      );

      toast({
        title: "Template Imported!",
        description: `"${sharedTemplate.name}" by ${sharedTemplate.author} has been added to your templates`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Reload user's templates
      loadFollowupTemplates();
    } catch (error) {
      toast({
        title: "Import Error",
        description: error.message || "Failed to import template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Get backend base URL
  const getBackendBaseUrl = () => {
    const override = process.env.REACT_APP_BACKEND_URL;
    if (override && override.trim() !== "") return override;
    const hostname = window.location.hostname;
    return hostname === "localhost" || hostname === "127.0.0.1"
      ? BASE_URLS.local.backend
      : BASE_URLS.render.backend;
  };

  // Helper function to convert Quill delta to plain text
  const convertDeltaToPlainText = (delta) => {
    if (!delta || !delta.ops) return "";
    return delta.ops
      .map((op) => {
        if (typeof op.insert === "string") {
          return op.insert;
        }
        return "";
      })
      .join("")
      .replace(/\n/g, " ")
      .trim();
  };

  // Function to fetch patient document content (for single patient mode)
  const fetchPatientDocument = async () => {
    if (
      !patientData ||
      (!patientData.patientRecord && !patientData.appointmentDate)
    ) {
      console.log(
        "[FollowupModal] No patient record or appointment date available for document fetch",
      );
      return null;
    }

    const patient = patientData.patientRecord || {};
    const patientName = patientData.patientName;
    const visitDate = patientData.appointmentDate;

    if (!patientName || !visitDate) {
      console.log("[FollowupModal] Missing patientName or visitDate");
      return null;
    }

    try {
      // Find the EHR_ID field index
      const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
      const ehrIdField = ehrIdIndex >= 0 ? prompts[ehrIdIndex][0] : null;

      // Use EHR_ID if available, otherwise fall back to database id
      const patientIdForFile =
        ehrIdField && patient[ehrIdField] ? patient[ehrIdField] : patient.id;

      // Construct the file path
      const fileName = `${patientName}-${patientIdForFile}.json`;
      const year = visitDate.split("-")[0];
      const filePath = await generateMedADocsPath(
        year,
        visitDate,
        fileName,
        effectiveSchema,
      );

      console.log("[FollowupModal] Fetching document:", {
        fileName,
        filePath,
        year,
        visitDate,
      });

      const documentData = await fetchDocument(bucketName_files, filePath);

      if (documentData && documentData.delta) {
        const plainText = convertDeltaToPlainText(documentData.delta);
        console.log(
          "[FollowupModal] Document fetched successfully, length:",
          plainText.length,
        );
        return plainText;
      }

      return null;
    } catch (error) {
      console.error("[FollowupModal] Error fetching patient document:", error);
      return null;
    }
  };

  // Function to fetch document for a specific patient (for multiple patients mode)
  const fetchDocumentForPatient = async (patient) => {
    if (!patient || !patient.patientName || !patient.appointmentDate) {
      console.log(
        "[FollowupModal] Missing patientName or appointmentDate for document fetch",
      );
      return null;
    }

    try {
      // Find the EHR_ID field index
      const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
      const ehrIdField = ehrIdIndex >= 0 ? prompts[ehrIdIndex][0] : null;

      // Use EHR_ID if available, otherwise fall back to patientId
      const patientIdForFile =
        ehrIdField && patient.patientId
          ? patient.patientId
          : patient.patientId || patient.id || "unknown";

      // Construct the file path
      const fileName = `${patient.patientName}-${patientIdForFile}.json`;
      const year = patient.appointmentDate.split("-")[0];
      const filePath = await generateMedADocsPath(
        year,
        patient.appointmentDate,
        fileName,
        effectiveSchema,
      );

      console.log("[FollowupModal] Fetching document for patient:", {
        patientName: patient.patientName,
        fileName,
        filePath,
        year,
        appointmentDate: patient.appointmentDate,
      });

      const documentData = await fetchDocument(bucketName_files, filePath);

      if (documentData && documentData.delta) {
        const plainText = convertDeltaToPlainText(documentData.delta);
        console.log(
          "[FollowupModal] Document fetched for",
          patient.patientName,
          ", length:",
          plainText.length,
        );
        return plainText;
      }

      console.log(
        "[FollowupModal] No document delta found for",
        patient.patientName,
      );
      return null;
    } catch (error) {
      console.error(
        "[FollowupModal] Error fetching document for",
        patient.patientName,
        ":",
        error,
      );
      return null;
    }
  };

  // Reset state when modal opens with new patient data
  useEffect(() => {
    if (isOpen && patientData) {
      // Reset state at the start
      setGeneratedMessage("");
      setEditedMessage("");
      setDocumentContent("");
      setShowPromptEditor(false);

      // Load current user name to check if they are the author of shared templates
      getCurrentUserName().then((userName) => {
        setCurrentUserName(userName);
      });

      // Load shared templates to know which templates are already shared
      loadSharedTemplatesFromShared();

      // For single patient mode: auto-generate followup when modal opens
      // For multiple patients: Don't auto-generate - let user click "Generate All" or "Generate for Current"
      if (!isMultiplePatients) {
        loadFollowupTemplates((promptData) => {
          console.log("[FollowupModal] About to generate with loaded prompt:", {
            hasPrompt: !!promptData,
            promptPreview: promptData?.fullPrompt?.substring(0, 80),
            templatePreview: promptData?.templateInfo?.substring(0, 80),
          });
          fetchDocumentAndGenerate(promptData);
        });
      }
    }
  }, [isOpen, patientData]);

  // Fetch document and then generate followup message
  const fetchDocumentAndGenerate = async (overridePrompt = null) => {
    setIsFetchingDoc(true);
    try {
      // Fetch patient document
      const docContent = await fetchPatientDocument();
      if (docContent) {
        setDocumentContent(docContent);
        console.log(
          "[FollowupModal] Document fetched, length:",
          docContent.length,
        );
      } else {
        console.log("[FollowupModal] No document found for patient");
      }
    } catch (error) {
      console.error("[FollowupModal] Error fetching document:", error);
    } finally {
      setIsFetchingDoc(false);
      // Now generate the followup message with optional override prompt
      generateFollowupMessage(overridePrompt);
    }
  };

  // Generate follow-up message using the AI endpoint
  const generateFollowupMessage = async (overridePrompt = null) => {
    // Get the current active patient based on tab index
    const currentPatient = isMultiplePatients
      ? patientsList[activePatientIndex]
      : patientData;

    if (
      !currentPatient ||
      !currentPatient.patientName ||
      !currentPatient.patientPhone
    ) {
      toast({
        title: "Error",
        description:
          "Patient name and phone number are required for message generation",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const backendBaseUrl = getBackendBaseUrl();

      // Build request payload with document content if available
      const requestPayload = {
        patient_name: currentPatient.patientName,
        phone_number: currentPatient.patientPhone,
        patient_id: currentPatient.patientId || null,
        schema: effectiveSchema,
      };

      // Include document content if available (we need to fetch for this patient)
      // For now, we'll try to use existing documentContent for single patient mode
      // In multi-patient mode, we'd need to fetch per-patient documents
      if (!isMultiplePatients && documentContent) {
        requestPayload.document_content = documentContent;
        console.log(
          "[FollowupModal] Including document content in request, length:",
          documentContent.length,
        );
      }

      // Use custom prompt if provided (either from override or state)
      const effectivePrompt =
        (overridePrompt && overridePrompt.fullPrompt) ||
        (customPrompt && customPrompt.fullPrompt);
      const effectiveTemplate =
        (overridePrompt && overridePrompt.templateInfo) ||
        (customPrompt && customPrompt.templateInfo) ||
        "";

      console.log("[FollowupModal] Generation debug:", {
        overridePrompt: overridePrompt?.fullPrompt ? "provided" : "null",
        customPrompt: customPrompt?.fullPrompt ? "provided" : "null",
        effectivePrompt: effectivePrompt ? "using custom" : "using default",
        templateLength: effectiveTemplate?.length || 0,
      });

      if (effectivePrompt) {
        requestPayload.custom_prompt = effectivePrompt;
        requestPayload.prompt_template = effectiveTemplate;
        console.log(
          "[FollowupModal] Using custom prompt, length:",
          effectivePrompt.length,
        );
      }

      const response = await fetch(
        `${backendBaseUrl}/api/twilio/generate-followup/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestPayload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to generate follow-up message",
        );
      }

      const data = await response.json();
      const generatedMsg = data.generated_message || "";

      // Store message per patient (for multiple) or as single message (for backward compatibility)
      if (isMultiplePatients) {
        setPatientMessages((prev) => ({
          ...prev,
          [currentPatient.patientId]: {
            generated: generatedMsg,
            edited: generatedMsg,
          },
        }));
        // Mark this patient as generated
        if (!generatedPatients.includes(currentPatient.patientId)) {
          setGeneratedPatients((prev) => [...prev, currentPatient.patientId]);
        }
      } else {
        setGeneratedMessage(generatedMsg);
        setEditedMessage(generatedMsg);
      }

      toast({
        title: "Message Generated",
        description: isMultiplePatients
          ? `AI generated follow-up message for ${currentPatient.patientName}`
          : "AI has generated a follow-up message for review",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error generating follow-up message:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate follow-up message",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Generate follow-up messages for ALL selected patients at once
  const generateAllFollowupMessages = async () => {
    if (!isMultiplePatients) {
      // For single patient mode, just call the regular function
      generateFollowupMessage();
      return;
    }

    // Get all patients with phone numbers
    const patientsToGenerate = patientsList.filter((p) => p.patientPhone);

    if (patientsToGenerate.length === 0) {
      toast({
        title: "No Patients",
        description: "No patients with phone numbers to generate messages for",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Set generation progress
    setGenerationProgress({
      current: 0,
      total: patientsToGenerate.length,
      isGenerating: true,
    });

    setIsBulkGenerating(true);

    const backendBaseUrl = getBackendBaseUrl();
    const failedGenerations = [];

    // Generate message for each patient
    for (let i = 0; i < patientsToGenerate.length; i++) {
      const patient = patientsToGenerate[i];

      // Update current patient index so UI shows correct tab
      setActivePatientIndex(
        patientsList.findIndex((p) => p.patientId === patient.patientId),
      );

      try {
        // Fetch document for this patient first
        let documentContent = null;
        try {
          documentContent = await fetchDocumentForPatient(patient);
          if (documentContent) {
            console.log(
              "[GenerateAll] Document fetched for",
              patient.patientName,
              ", length:",
              documentContent.length,
            );
          } else {
            console.log(
              "[GenerateAll] No document found for",
              patient.patientName,
            );
          }
        } catch (docError) {
          console.error(
            "[GenerateAll] Error fetching document for",
            patient.patientName,
            ":",
            docError,
          );
          // Continue without document - the backend will use database records
        }

        // Build request payload
        const requestPayload = {
          patient_name: patient.patientName,
          phone_number: patient.patientPhone,
          patient_id: patient.patientId || null,
          schema: effectiveSchema,
        };

        // Include document content if available
        if (documentContent) {
          requestPayload.document_content = documentContent;
          console.log(
            "[GenerateAll] Including document content in request for",
            patient.patientName,
            ", length:",
            documentContent.length,
          );
        }

        // Use custom prompt if provided
        const effectivePrompt = customPrompt && customPrompt.fullPrompt;
        const effectiveTemplate = customPrompt && customPrompt.templateInfo;

        if (effectivePrompt) {
          requestPayload.custom_prompt = effectivePrompt;
          requestPayload.prompt_template = effectiveTemplate || "";
        }

        const response = await fetch(
          `${backendBaseUrl}/api/twilio/generate-followup/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestPayload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to generate follow-up message",
          );
        }

        const data = await response.json();
        const generatedMsg = data.generated_message || "";
        console.log(
          "[GenerateAll] Generated message for",
          patient.patientName,
          ":",
          generatedMsg.substring(0, 50),
        );

        // Store message for this patient
        setPatientMessages((prev) => ({
          ...prev,
          [patient.patientId]: {
            generated: generatedMsg,
            edited: generatedMsg,
          },
        }));

        // Mark this patient as generated
        if (!generatedPatients.includes(patient.patientId)) {
          setGeneratedPatients((prev) => [...prev, patient.patientId]);
        }

        // Update generation progress
        setGenerationProgress((prev) => ({
          ...prev,
          current: i + 1,
        }));
      } catch (error) {
        console.error(`Error generating for ${patient.patientName}:`, error);
        failedGenerations.push({
          patientName: patient.patientName,
          error: error.message,
        });

        // Update progress even for failed
        setGenerationProgress((prev) => ({
          ...prev,
          current: i + 1,
        }));
      }
    }

    // Finish generation
    setGenerationProgress((prev) => ({
      ...prev,
      isGenerating: false,
    }));
    setIsBulkGenerating(false);

    // Show results toast
    if (failedGenerations.length === 0) {
      toast({
        title: "Messages Generated",
        description: `Successfully generated follow-up messages for ${patientsToGenerate.length} patient(s)`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else if (failedGenerations.length === patientsToGenerate.length) {
      toast({
        title: "Generation Failed",
        description: `Failed to generate messages for all ${failedGenerations.length} patient(s)`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Partial Success",
        description: `Generated for ${patientsToGenerate.length - failedGenerations.length} patient(s), failed for ${failedGenerations.length} patient(s)`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Send the follow-up message (supports single or multiple patients)
  const handleSend = async () => {
    // Single-patient mode stores draft in editedMessage; multi-patient uses patientMessages per id
    const messageForPatient = (p) =>
      isMultiplePatients
        ? patientMessages[p.patientId]?.edited?.trim() || ""
        : editedMessage?.trim() || "";

    // Get selected patients (only those with phone numbers)
    const selectedPatients = patientsList.filter(
      (p) => selectedPatientsForSending.includes(p.patientId) && p.patientPhone,
    );

    if (selectedPatients.length === 0) {
      toast({
        title: "No Recipients",
        description:
          "Please select at least one patient to send the message to",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check that all selected patients have messages generated
    const missingMessages = selectedPatients.filter(
      (p) => !messageForPatient(p),
    );
    if (missingMessages.length > 0) {
      toast({
        title: "Missing Messages",
        description: `Please generate messages for: ${missingMessages.map((p) => p.patientName).join(", ")}`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // If multiple patients, show confirmation
    if (selectedPatients.length > 1) {
      const proceed = window.confirm(
        `You are about to send individual messages to ${selectedPatients.length} patients. Each patient will receive their own personalized message. Continue?`,
      );
      if (!proceed) return;
    }

    const badNumberGuard = await confirmBadNumbers(
      selectedPatients.map((p) => ({ phone: p.patientPhone, name: p.patientName })),
    );
    if (!badNumberGuard) return;

    setIsSending(true);

    // Initialize progress tracking
    setSendProgress({
      current: 0,
      total: selectedPatients.length,
      isSending: true,
      completed: [],
      failed: [],
    });

    const backendBaseUrl = getBackendBaseUrl();
    const sendResults = {
      completed: [],
      failed: [],
    };

    // Send to each selected patient with their own message
    for (let i = 0; i < selectedPatients.length; i++) {
      const patient = selectedPatients[i];

      // Get the message for this specific patient
      const patientMessage = messageForPatient(patient);

      if (badNumberGuard.skipped(patient.patientPhone)) {
        sendResults.failed.push({
          patientId: patient.patientId,
          patientName: patient.patientName,
          error: "Skipped - known bad number",
        });
        setSendProgress((prev) => ({
          ...prev,
          current: i + 1,
          failed: [...prev.failed, patient.patientId],
        }));
        continue;
      }

      try {
        const response = await fetch(`${backendBaseUrl}/twilio/send-sms/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            to: patient.patientPhone,
            message: patientMessage,
            patientId: patient.patientId || null,
            schema: effectiveSchema,
            confirm_bad_number: badNumberGuard.confirmed(patient.patientPhone),
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (
            errorData.code === "DNT_BLOCKED" ||
            errorData.error?.includes("Do Not Text")
          ) {
            sendResults.failed.push({
              patientId: patient.patientId,
              patientName: patient.patientName,
              error: "Do Not Text - blocked",
            });
            // Update progress
            setSendProgress((prev) => ({
              ...prev,
              current: i + 1,
              failed: [...prev.failed, patient.patientId],
            }));
            continue;
          }
          throw new Error(errorData.error || "Failed to send message");
        }

        // Success
        sendResults.completed.push({
          patientId: patient.patientId,
          patientName: patient.patientName,
          patientPhone: patient.patientPhone,
        });

        // Update progress
        setSendProgress((prev) => ({
          ...prev,
          current: i + 1,
          completed: [...prev.completed, patient.patientId],
        }));
      } catch (error) {
        console.error(`Error sending to ${patient.patientName}:`, error);
        sendResults.failed.push({
          patientId: patient.patientId,
          patientName: patient.patientName,
          error: error.message,
        });

        // Update progress
        setSendProgress((prev) => ({
          ...prev,
          current: i + 1,
          failed: [...prev.failed, patient.patientId],
        }));
      }
    }

    // Finalize progress
    setSendProgress((prev) => ({
      ...prev,
      isSending: false,
    }));

    setIsSending(false);

    // Show appropriate toast
    if (sendResults.failed.length === 0) {
      toast({
        title: "Messages Sent",
        description: `Successfully sent follow-up message to ${sendResults.completed.length} patient(s)`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else if (sendResults.completed.length === 0) {
      toast({
        title: "Sending Failed",
        description: `Failed to send message to all ${sendResults.failed.length} patient(s)`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Partial Success",
        description: `Sent to ${sendResults.completed.length} patient(s), failed for ${sendResults.failed.length} patient(s)`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }

    // Call onSend callback if provided
    if (onSend) {
      onSend({
        total: selectedPatients.length,
        completed: sendResults.completed,
        failed: sendResults.failed,
        // For multi-patient, pass the count; for single, pass the message
        message: isMultiplePatients
          ? `${selectedPatients.length} patients`
          : editedMessage,
      });
    }

    // Close the modal
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    setGeneratedMessage("");
    setEditedMessage("");
    onClose();
  };

  // Format appointment date for display
  const formatAppointmentDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString + "T12:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        size="lg"
        isCentered
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>
            <VStack align="start" spacing={1}>
              <Text>
                {isMultiplePatients
                  ? `Send Follow-up (${patientsList.length} patients)`
                  : "Send Follow-up Message"}
              </Text>
              {isMultiplePatients ? (
                <HStack spacing={2} mt={1} wrap="wrap">
                  {patientsList.slice(0, 3).map((p) => (
                    <Badge key={p.patientId} colorScheme="blue" fontSize="xs">
                      {p.patientName}
                    </Badge>
                  ))}
                  {patientsList.length > 3 && (
                    <Badge colorScheme="gray" fontSize="xs">
                      +{patientsList.length - 3} more
                    </Badge>
                  )}
                </HStack>
              ) : patientData ? (
                <HStack spacing={2} mt={1}>
                  <Badge colorScheme="blue" fontSize="sm">
                    {patientData.patientName}
                  </Badge>
                  <Badge colorScheme="gray" fontSize="xs">
                    {patientData.patientPhone}
                  </Badge>
                  <PhoneStatusBadge phone={patientData.patientPhone} />
                </HStack>
              ) : null}
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Patient Selection for Multiple Patients */}
              {isMultiplePatients && (
                <Box
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="blue.200"
                >
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                      Select Recipients ({selectedPatientsForSending.length} of{" "}
                      {patientsList.filter((p) => p.patientPhone).length} with
                      phone)
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {patientsList.map((patient) => (
                        <Box
                          key={patient.patientId}
                          p={2}
                          bg={
                            selectedPatientsForSending.includes(
                              patient.patientId,
                            ) && patient.patientPhone
                              ? "blue.100"
                              : "white"
                          }
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={
                            selectedPatientsForSending.includes(
                              patient.patientId,
                            ) && patient.patientPhone
                              ? "blue.300"
                              : "gray.200"
                          }
                          opacity={patient.patientPhone ? 1 : 0.5}
                        >
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <HStack>
                                <Text fontSize="sm" fontWeight="medium">
                                  {patient.patientName}
                                </Text>
                                {generatedPatients.includes(
                                  patient.patientId,
                                ) && (
                                  <Badge colorScheme="green" fontSize="xs">
                                    Generated
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.500">
                                {patient.patientPhone || "No phone"}
                                <PhoneStatusBadge phone={patient.patientPhone} compact ml={1} />
                              </Text>
                            </VStack>
                            {patient.patientPhone && (
                              <Checkbox
                                isChecked={selectedPatientsForSending.includes(
                                  patient.patientId,
                                )}
                                onChange={() => {
                                  setSelectedPatientsForSending((prev) =>
                                    prev.includes(patient.patientId)
                                      ? prev.filter(
                                          (id) => id !== patient.patientId,
                                        )
                                      : [...prev, patient.patientId],
                                  );
                                }}
                                colorScheme="blue"
                              />
                            )}
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                    {sendProgress.isSending && (
                      <Box>
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          Sending... {sendProgress.current} of{" "}
                          {sendProgress.total}
                        </Text>
                        <Progress
                          value={
                            (sendProgress.current / sendProgress.total) * 100
                          }
                          size="sm"
                          colorScheme="blue"
                          borderRadius="full"
                        />
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}

              {/* Tabs for Multiple Patients - Show when multiple patients */}
              {isMultiplePatients && (
                <Tabs
                  index={activePatientIndex}
                  onChange={setActivePatientIndex}
                  variant="enclosed"
                  colorScheme="purple"
                  size="sm"
                >
                  <TabList flexWrap="wrap">
                    {patientsList.map((patient, index) => (
                      <Tab key={patient.patientId} fontSize="xs" py={1} px={2}>
                        {patient.patientName}
                        {generatedPatients.includes(patient.patientId) && (
                          <Badge ml={1} colorScheme="green" fontSize="xs">
                            ✓
                          </Badge>
                        )}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>
              )}

              {/* Current Patient Info for Multi-Patient Mode */}
              {isMultiplePatients && patientsList[activePatientIndex] && (
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <HStack justify="space-between" wrap="wrap" gap={2}>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color="gray.500" fontWeight="medium">
                        Appointment Date
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {formatAppointmentDate(
                          patientsList[activePatientIndex].appointmentDate,
                        )}
                      </Text>
                    </VStack>
                    {patientsList[activePatientIndex].appointmentType && (
                      <VStack align="start" spacing={0}>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                        >
                          Type
                        </Text>
                        <Badge colorScheme="purple" fontSize="sm">
                          {patientsList[activePatientIndex].appointmentType}
                        </Badge>
                      </VStack>
                    )}
                  </HStack>
                </Box>
              )}

              {/* Patient Info (for single patient) */}
              {patientData && !isMultiplePatients && (
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <HStack justify="space-between" wrap="wrap" gap={2}>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color="gray.500" fontWeight="medium">
                        Appointment Date
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {formatAppointmentDate(patientData.appointmentDate)}
                      </Text>
                    </VStack>
                    {patientData.appointmentType && (
                      <VStack align="start" spacing={0}>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="medium"
                        >
                          Type
                        </Text>
                        <Badge colorScheme="purple" fontSize="sm">
                          {patientData.appointmentType}
                        </Badge>
                      </VStack>
                    )}
                  </HStack>
                </Box>
              )}

              <Divider />

              {/* Document Fetching State */}
              {isFetchingDoc && (
                <Box py={8} textAlign="center">
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text mt={4} color="gray.600">
                    Fetching patient document...
                  </Text>
                </Box>
              )}

              {/* Loading State */}
              {(isLoading || isBulkGenerating) && !isFetchingDoc && (
                <Box py={8} textAlign="center">
                  <Spinner size="xl" color="purple.500" thickness="4px" />
                  <Text mt={4} color="gray.600">
                    {isBulkGenerating
                      ? `Generating follow-up messages... ${generationProgress.current}/${generationProgress.total}`
                      : "Generating AI follow-up message..."}
                  </Text>
                  {isBulkGenerating && (
                    <Progress
                      mt={3}
                      value={
                        (generationProgress.current /
                          generationProgress.total) *
                        100
                      }
                      size="sm"
                      colorScheme="green"
                      borderRadius="full"
                      width="200px"
                      mx="auto"
                    />
                  )}
                </Box>
              )}

              {/* Message Textarea */}
              {!isLoading && !isFetchingDoc && (
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.700">
                      Follow-up Message
                    </Text>
                    <HStack>
                      {isMultiplePatients && (
                        <Button
                          size="xs"
                          variant="solid"
                          colorScheme="green"
                          onClick={() => {
                            console.log("Generate All button clicked!");
                            console.log(
                              "isMultiplePatients:",
                              isMultiplePatients,
                            );
                            console.log("patientsList:", patientsList);
                            generateAllFollowupMessages();
                          }}
                          isLoading={isBulkGenerating}
                          title="Generate messages for all patients at once"
                        >
                          {isBulkGenerating
                            ? `Generating ${generationProgress.current}/${generationProgress.total}`
                            : `Generate All (${patientsList.filter((p) => p.patientPhone).length})`}
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => generateFollowupMessage()}
                        isLoading={
                          isLoading || isFetchingDoc || isBulkGenerating
                        }
                      >
                        {isMultiplePatients
                          ? "Generate for Current"
                          : "Generate"}
                      </Button>
                      {isMultiplePatients && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() => {
                            // Generate message for the current patient
                            generateFollowupMessage();
                          }}
                          isLoading={
                            isLoading || isFetchingDoc || isBulkGenerating
                          }
                          title="Regenerate message for current patient"
                        >
                          Regenerate
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                  <Textarea
                    ref={messageTextareaRef}
                    value={
                      isMultiplePatients && patientsList[activePatientIndex]
                        ? patientMessages[
                            patientsList[activePatientIndex].patientId
                          ]?.edited || ""
                        : editedMessage
                    }
                    onChange={(e) => {
                      if (
                        isMultiplePatients &&
                        patientsList[activePatientIndex]
                      ) {
                        const patientId =
                          patientsList[activePatientIndex].patientId;
                        setPatientMessages((prev) => ({
                          ...prev,
                          [patientId]: {
                            ...prev[patientId],
                            edited: e.target.value,
                          },
                        }));
                      } else {
                        setEditedMessage(e.target.value);
                      }
                    }}
                    placeholder="Enter your follow-up message..."
                    rows={10}
                    resize="vertical"
                    fontSize="sm"
                    focusBorderColor="purple.500"
                  />
                  <Text fontSize="xs" color="gray.500">
                    {isMultiplePatients
                      ? `Message for ${patientsList[activePatientIndex]?.patientName}. Click tabs to switch between patients.`
                      : "You can edit the message before sending"}
                  </Text>
                </VStack>
              )}

              {/* Prompt Editor - Collapsible */}
              <Box>
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="purple"
                  onClick={() => {
                    if (!showPromptEditor) {
                      loadFollowupTemplates();
                      loadSharedTemplatesFromShared();
                    }
                    setShowPromptEditor(!showPromptEditor);
                  }}
                >
                  {showPromptEditor
                    ? "▼ Hide Prompt Editor"
                    : "- Show Prompt Editor"}
                </Button>

                {/* Global System Prompt Manager Button */}
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => setShowGlobalPromptManager(true)}
                  ml={2}
                  title="Set global system prompt for all users"
                >
                  Global System
                </Button>

                {/* Template Shared Button */}
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="teal"
                  onClick={() => {
                    setShowShared(true);
                    loadSharedTemplatesFromShared();
                  }}
                  ml={2}
                  title="Browse shared templates from other users"
                >
                  Shared{" "}
                  {sharedTemplates.length > 0 && `(${sharedTemplates.length})`}
                </Button>

                <Collapse in={showPromptEditor}>
                  <Box mt={2} p={3} bg="gray.50" borderRadius="md">
                    <VStack align="stretch" spacing={3}>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.700"
                      >
                        Custom Prompt:
                      </Text>
                      <Textarea
                        value={editablePrompt}
                        onChange={(e) => setEditablePrompt(e.target.value)}
                        placeholder="Enter your prompt here..."
                        rows={5}
                        resize="vertical"
                        fontFamily="mono"
                        fontSize="xs"
                      />

                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.700"
                      >
                        Template Info:
                      </Text>
                      <Textarea
                        value={templateInfo}
                        onChange={(e) => setTemplateInfo(e.target.value)}
                        placeholder="Enter template here..."
                        rows={3}
                        resize="vertical"
                        fontFamily="mono"
                        fontSize="xs"
                      />

                      <HStack wrap="wrap" gap={2}>
                        <Input
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                          placeholder="Template name..."
                          size="sm"
                          width="150px"
                        />
                        {editingTemplateFileName ? (
                          <Button
                            colorScheme="green"
                            onClick={handleEditTemplate}
                            isDisabled={
                              !editablePrompt.trim() || !templateName.trim()
                            }
                            size="sm"
                          >
                            Update
                          </Button>
                        ) : (
                          <Button
                            colorScheme="blue"
                            onClick={handleSavePromptTemplate}
                            isDisabled={
                              !editablePrompt.trim() || !templateName.trim()
                            }
                            size="sm"
                          >
                            Save
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={handleUsePrompt}
                          isDisabled={!editablePrompt.trim()}
                          size="sm"
                        >
                          Generate
                        </Button>
                        <Button
                          variant="ghost"
                          colorScheme="gray"
                          onClick={() => {
                            setEditablePrompt(getDefaultPrompt());
                            setTemplateInfo(getDefaultTemplate());
                            setTemplateName("");
                            setEditingTemplateFileName(null);
                            setCustomPrompt(null);
                            toast({
                              title: "Reset Complete",
                              description: "Prompt has been reset to default",
                              status: "info",
                              duration: 2000,
                              isClosable: true,
                            });
                          }}
                          size="sm"
                        >
                          Reset
                        </Button>
                        <Button
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => {
                            // Clear only the template name and editing state to create a new template
                            setTemplateName("");
                            setEditingTemplateFileName(null);
                            toast({
                              title: "New Template",
                              description: "Start creating a new template",
                              status: "info",
                              duration: 2000,
                              isClosable: true,
                            });
                          }}
                          size="sm"
                        >
                          Add New
                        </Button>
                        <Button
                          variant="outline"
                          colorScheme="red"
                          onClick={async () => {
                            try {
                              console.log(
                                "[Reset to Default] Starting reset process...",
                              );

                              // First, clear the user's saved default template
                              // This ensures the user's custom default is removed
                              console.log(
                                "[Reset to Default] Clearing user default template...",
                              );
                              await clearDefaultTemplate("followup");

                              // Clear local state variables that track default
                              console.log(
                                "[Reset to Default] Clearing local state variables...",
                              );
                              setDefaultTemplateFileName(null);
                              setUserDefaultFileName(null);

                              console.log(
                                "[Reset to Default] Loading global system template...",
                              );
                              // Now check for global system template
                              const globalTemplate =
                                await loadGlobalSystemTemplate("followup");

                              let defaultPrompt;
                              let defaultTemplate;
                              let templateSource = "hardcoded";

                              if (
                                globalTemplate.templateText &&
                                globalTemplate.promptText
                              ) {
                                // Use global system template
                                defaultPrompt = globalTemplate.promptText;
                                defaultTemplate = globalTemplate.templateText;
                                templateSource = "global";
                                console.log(
                                  "[Reset to Default] Using global system template",
                                );
                              } else {
                                // Fall back to hardcoded defaults
                                defaultPrompt = getDefaultPrompt();
                                defaultTemplate = getDefaultTemplate();
                                console.log(
                                  "[Reset to Default] Using hardcoded defaults",
                                );
                              }

                              // Set local state to use the global/system default (but don't save as user's default)
                              setEditablePrompt(defaultPrompt);
                              setTemplateInfo(defaultTemplate);
                              setTemplateName(
                                templateSource === "global"
                                  ? "Global System"
                                  : "System Default",
                              );
                              setEditingTemplateFileName(null);
                              setDefaultTemplateFileName(null);

                              // Set customPrompt so the default is used for generation
                              const promptData = {
                                fullPrompt: defaultPrompt,
                                editablePrompt: defaultPrompt,
                                templateInfo: defaultTemplate,
                              };
                              setCustomPrompt(promptData);

                              toast({
                                title: "Reset Complete",
                                description:
                                  templateSource === "global"
                                    ? "Now using global system template (user default cleared)"
                                    : "Now using system default (user default cleared)",
                                status: "success",
                                duration: 3000,
                                isClosable: true,
                              });

                              console.log(
                                "[Reset to Default] Regenerating message with reset template...",
                              );
                              // Regenerate message with the reset template if document exists
                              if (documentContent) {
                                fetchDocumentAndGenerate(promptData);
                              }
                            } catch (error) {
                              console.error(
                                "Error resetting default template:",
                                error,
                              );
                              toast({
                                title: "Error",
                                description: "Failed to reset default template",
                                status: "error",
                                duration: 3000,
                                isClosable: true,
                              });
                            }
                          }}
                          size="sm"
                        >
                          Reset to Default
                        </Button>
                      </HStack>

                      {/* Saved Templates */}
                      {savedFollowupTemplates.length > 0 && (
                        <Box>
                          <Text
                            fontWeight="semibold"
                            mb={1}
                            fontSize="xs"
                            color="gray.600"
                          >
                            Saved Templates:
                          </Text>
                          <VStack align="stretch" spacing={2}>
                            {savedFollowupTemplates.map((template) => (
                              <HStack
                                key={template.fileName}
                                wrap="wrap"
                                gap={1}
                              >
                                <Button
                                  size="xs"
                                  variant={
                                    editingTemplateFileName ===
                                    template.fileName
                                      ? "solid"
                                      : "outline"
                                  }
                                  colorScheme={
                                    editingTemplateFileName ===
                                    template.fileName
                                      ? "purple"
                                      : "gray"
                                  }
                                  onClick={() =>
                                    loadTemplate(template.fileName)
                                  }
                                >
                                  {template.name}
                                </Button>
                                {/* Show green badge if either defaultTemplateFileName matches OR userDefaultFileName matches (handles the case where template content doesn't match but we have a saved default file name) */}
                                {defaultTemplateFileName ===
                                  template.fileName ||
                                userDefaultFileName === template.fileName ? (
                                  <Button
                                    size="xs"
                                    variant="solid"
                                    colorScheme="green"
                                    isDisabled
                                    title="Currently set as default"
                                  >
                                    Default
                                  </Button>
                                ) : (
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={() =>
                                      handleSetDefault(template.fileName)
                                    }
                                    title="Set as default"
                                  >
                                    Default
                                  </Button>
                                )}
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() =>
                                    handleDeleteTemplate(template.fileName)
                                  }
                                  title="Delete template"
                                >
                                  Delete
                                </Button>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  colorScheme="teal"
                                  onClick={() => {
                                    const sharedInfo =
                                      sharedTemplatesMap[template.fileName];
                                    const isShared = !!sharedInfo;
                                    const isOwnTemplate =
                                      isShared &&
                                      currentUserName &&
                                      sharedInfo.author === currentUserName;

                                    if (isOwnTemplate) {
                                      // User is the author - allow unshare
                                      handleUnshareTemplate({
                                        name: template.name,
                                        fileName: template.fileName,
                                      });
                                    } else if (isShared) {
                                      // Template is shared but by another user - do nothing
                                      toast({
                                        title: "Cannot Unshare",
                                        description:
                                          "You can only unshare templates that you have shared",
                                        status: "warning",
                                        duration: 3000,
                                        isClosable: true,
                                      });
                                    } else {
                                      // Not shared - share it
                                      handleShareTemplate(template);
                                    }
                                  }}
                                  isDisabled={isSharing}
                                  title={(() => {
                                    const sharedInfo =
                                      sharedTemplatesMap[template.fileName];
                                    const isShared = !!sharedInfo;
                                    const isOwnTemplate =
                                      isShared &&
                                      currentUserName &&
                                      sharedInfo.author === currentUserName;
                                    if (isOwnTemplate)
                                      return "Click to remove from shared templates";
                                    if (isShared)
                                      return "Shared by another user - cannot unshare";
                                    return "Share this template with all users";
                                  })()}
                                >
                                  {(() => {
                                    const sharedInfo =
                                      sharedTemplatesMap[template.fileName];
                                    const isShared = !!sharedInfo;
                                    const isOwnTemplate =
                                      isShared &&
                                      currentUserName &&
                                      sharedInfo.author === currentUserName;
                                    if (isOwnTemplate) return "Shared ✓";
                                    if (isShared) return "Shared";
                                    return "Share";
                                  })()}
                                </Button>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </Collapse>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={isSending}
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleSend}
                isLoading={isSending}
                isDisabled={
                  isLoading ||
                  isFetchingDoc ||
                  isBulkGenerating ||
                  selectedPatientsForSending.length === 0 ||
                  (!isMultiplePatients && !editedMessage?.trim()) ||
                  (isMultiplePatients &&
                    selectedPatientsForSending.some(
                      (id) => !patientMessages[id]?.edited?.trim(),
                    ))
                }
              >
                {isMultiplePatients
                  ? `Send to ${selectedPatientsForSending.length} Patient${selectedPatientsForSending.length !== 1 ? "s" : ""}`
                  : "Send Follow-up"}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Global System Prompt Manager Modal */}
      <GlobalSystemPromptManager
        isOpen={showGlobalPromptManager}
        onClose={() => setShowGlobalPromptManager(false)}
        patientType="followup"
      />

      {/* Template Shared Modal */}
      <Modal
        isOpen={showShared}
        onClose={() => setShowShared(false)}
        size="xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent maxH="80vh" overflowY="auto">
          <ModalHeader>
            <VStack align="start" spacing={1}>
              <Text>Template Shared</Text>
              <Text fontSize="xs" color="gray.500" fontWeight="normal">
                Browse and import templates shared by other users
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {isLoadingShared ? (
              <Box py={8} textAlign="center">
                <Spinner size="lg" color="teal.500" />
                <Text mt={4} color="gray.600">
                  Loading shared templates...
                </Text>
              </Box>
            ) : sharedTemplates.length === 0 ? (
              <Box py={8} textAlign="center">
                <Text color="gray.500" mb={2}>
                  No shared templates yet.
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Be the first to share your template to the Shared Templates!
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={3}>
                {sharedTemplates.map((template) => (
                  <Box
                    key={template.fileName}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "teal.300", bg: "gray.50" }}
                  >
                    <HStack justify="space-between" mb={1}>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {template.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          by {template.author}
                        </Text>
                      </VStack>
                      <Button
                        colorScheme="teal"
                        size="sm"
                        onClick={() => handleImportTemplate(template)}
                      >
                        Import
                      </Button>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" onClick={() => setShowShared(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FollowupMessageModal;
